// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: everything the task detail view does, shared by the phone sheet and
// the desktop modal. Every edit is attributed to the current user in ClickUp.

import KonvoTasks, {
	formatDuration,
	isTaskClosed,
	type KonvoSubtask,
	type KonvoTask,
	type KonvoTaskAttachment,
	type KonvoTaskDetail,
	type KonvoTaskPatch,
	type KonvoTimeEntry,
	KonvoTimeTrackingError,
} from '@app/features/konvo_tasks/state/KonvoTasks';
import {parseDuration} from '@app/features/konvo_tasks/utils/TaskTimeFormat';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {useClock} from '@app/features/ui/state/Tick';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const SAVED_DESCRIPTOR = msg({message: 'Saved to ClickUp.', comment: 'Toast after a task edit succeeds.'});
const SAVE_FAILED_DESCRIPTOR = msg({
	message: 'Could not save to ClickUp. Try again.',
	comment: 'Toast after a task edit fails.',
});
const COPIED_DESCRIPTOR = msg({message: 'Copied {id}', comment: 'Toast after copying the task id.'});
const LINK_COPIED_DESCRIPTOR = msg({message: 'Link copied', comment: 'Toast after copying the task link.'});
const TIMER_LINK_REQUIRED_DESCRIPTOR = msg({
	message: 'Connect your ClickUp account under Settings → Connections to use the timer.',
	comment: 'Task card hint when the timer is unavailable because ClickUp is not linked.',
});
const TIME_FAILED_DESCRIPTOR = msg({
	message: 'Could not update time tracking. Try again.',
	comment: 'Toast when a time tracking action fails.',
});
const INVALID_DURATION_DESCRIPTOR = msg({
	message: 'Enter between 1 minute and 24 hours, and a valid start date.',
	comment: 'Validation toast for the manual time entry input.',
});
const TIME_LOGGED_DESCRIPTOR = msg({message: 'Time logged in ClickUp.', comment: 'Toast after a time entry is saved.'});
const UPLOADING_DESCRIPTOR = msg({message: 'Uploading {name}…', comment: 'Task card toast while a file uploads.'});
const UPLOADED_DESCRIPTOR = msg({message: 'Attached {name}.', comment: 'Task card toast after a file upload.'});
const UPLOAD_FAILED_DESCRIPTOR = msg({
	message: 'Could not upload {name}. Files must be under 25 MB.',
	comment: 'Task card toast when a file upload fails.',
});

export {TIMER_LINK_REQUIRED_DESCRIPTOR};

/** Shared drag-and-drop / paste → upload → callback with the attachment. */
function useDropUpload(taskId: string, onUploaded: (attachment: KonvoTaskAttachment) => void) {
	const {i18n} = useLingui();
	const [dragging, setDragging] = useState(false);
	const [uploading, setUploading] = useState(false);
	const upload = useCallback(
		async (files: FileList | Array<File> | null) => {
			if (!files || files.length === 0) return;
			setUploading(true);
			for (const file of Array.from(files)) {
				ToastCommands.createToast({type: 'info', children: i18n._(UPLOADING_DESCRIPTOR, {name: file.name})});
				try {
					const attachment = await KonvoTasks.uploadAttachment(taskId, file);
					onUploaded(attachment);
					ToastCommands.createToast({type: 'success', children: i18n._(UPLOADED_DESCRIPTOR, {name: file.name})});
				} catch {
					ToastCommands.createToast({type: 'error', children: i18n._(UPLOAD_FAILED_DESCRIPTOR, {name: file.name})});
				}
			}
			setUploading(false);
		},
		[taskId, onUploaded, i18n],
	);
	const dropProps = {
		onDragOver: (event: React.DragEvent) => {
			if (event.dataTransfer.types.includes('Files')) {
				event.preventDefault();
				setDragging(true);
			}
		},
		onDragLeave: () => setDragging(false),
		onDrop: (event: React.DragEvent) => {
			if (!event.dataTransfer.files?.length) return;
			event.preventDefault();
			setDragging(false);
			void upload(event.dataTransfer.files);
		},
		onPaste: (event: React.ClipboardEvent) => {
			const files = Array.from(event.clipboardData.files ?? []);
			if (files.length > 0) {
				event.preventDefault();
				void upload(files);
			}
		},
	};
	return {dragging, uploading, upload, dropProps};
}

export type TaskDropUpload = ReturnType<typeof useDropUpload>;

export interface TimeLogDraft {
	/** Entry being edited; null when logging a new one. */
	entry: KonvoTimeEntry | null;
	duration: string;
	/** `datetime-local` value; empty means "now". */
	start: string;
	note: string;
}

const noopUploaded = () => {};

const EMPTY_DRAFT: TimeLogDraft = {entry: null, duration: '', start: '', note: ''};

/** `time: false` skips the time-tracking requests (the Home pop-up shows no time). */
export function useTaskWorkspace(taskId: string, {time = true}: {time?: boolean} = {}) {
	const {i18n} = useLingui();
	const [currentId, setCurrentId] = useState(taskId);
	const [loadError, setLoadError] = useState(false);
	const [saving, setSaving] = useState<string | null>(null);
	const detail: KonvoTaskDetail | undefined = KonvoTasks.details.get(currentId);
	const task: KonvoTask | null = detail?.task ?? KonvoTasks.getTask(currentId);
	const [title, setTitle] = useState(task?.name ?? '');
	const [description, setDescription] = useState(task?.description ?? '');
	const [descDirty, setDescDirty] = useState(false);
	const [comment, setComment] = useState('');
	const [timeDraft, setTimeDraft] = useState<TimeLogDraft>(EMPTY_DRAFT);

	useEffect(() => {
		KonvoTasks.setOpenTask(currentId);
		return () => {
			if (KonvoTasks.openTaskId === currentId) KonvoTasks.setOpenTask(null);
		};
	}, [currentId]);
	useEffect(() => {
		setLoadError(false);
		setDescDirty(false);
		setTimeDraft(EMPTY_DRAFT);
		void KonvoTasks.loadDetail(currentId).then((loaded) => {
			if (!loaded) setLoadError(true);
		});
		if (time) {
			void KonvoTasks.loadTime(currentId);
			void KonvoTasks.refreshRunningTimer();
		}
		KonvoClickUp.prefetch(true);
	}, [currentId, time]);
	useEffect(() => {
		if (task) {
			setTitle(task.name);
			if (!descDirty) setDescription(task.description);
		}
	}, [task?.name, task?.description, task, descDirty]);

	const toastSaveFailed = useCallback(
		() => ToastCommands.createToast({type: 'error', children: i18n._(SAVE_FAILED_DESCRIPTOR)}),
		[i18n],
	);

	const save = useCallback(
		async (label: string, patch: KonvoTaskPatch): Promise<boolean> => {
			setSaving(label);
			try {
				await KonvoTasks.updateTask(currentId, patch);
				ToastCommands.createToast({type: 'success', children: i18n._(SAVED_DESCRIPTOR)});
				return true;
			} catch {
				toastSaveFailed();
				return false;
			} finally {
				setSaving(null);
			}
		},
		[currentId, i18n, toastSaveFailed],
	);

	const commitTitle = useCallback(() => {
		const next = title.trim();
		if (!task || !next || next === task.name) {
			setTitle(task?.name ?? '');
			return;
		}
		void save('name', {name: next});
	}, [title, task, save]);

	const saveDescription = useCallback(async () => {
		if (await save('description', {description})) setDescDirty(false);
	}, [save, description]);
	const discardDescription = useCallback(() => {
		setDescription(task?.description ?? '');
		setDescDirty(false);
	}, [task?.description]);
	const editDescription = useCallback((value: string) => {
		setDescription(value);
		setDescDirty(true);
	}, []);

	const complete = useCallback(async () => {
		setSaving('status');
		try {
			await KonvoTasks.completeTask(currentId);
			ToastCommands.createToast({type: 'success', children: i18n._(SAVED_DESCRIPTOR)});
		} catch {
			toastSaveFailed();
		} finally {
			setSaving(null);
		}
	}, [currentId, i18n, toastSaveFailed]);

	const setStatus = useCallback(
		(status: string | null) => {
			if (status && task && status !== task.status.status) void save('status', {status});
		},
		[save, task],
	);
	const setPriority = useCallback(
		(priority: number | null) => {
			if (task && (task.priority?.id ?? null) !== priority) void save('priority', {priority});
		},
		[save, task],
	);
	const setDue = useCallback(
		(ms: number | null, withTime: boolean) => {
			void save('due', ms == null ? {due_date_ms: null} : {due_date_ms: ms, due_date_time: withTime});
		},
		[save],
	);
	const toggleAssignee = useCallback(
		(memberId: number) => {
			const assigned = task?.assignees.some((assignee) => assignee.id === memberId) ?? false;
			void save('assignees', {assignees: assigned ? {rem: [memberId]} : {add: [memberId]}});
		},
		[save, task?.assignees],
	);

	const submitComment = useCallback(async () => {
		const text = comment.trim();
		if (!text) return;
		setSaving('comment');
		try {
			await KonvoTasks.addComment(currentId, text);
			setComment('');
		} catch {
			toastSaveFailed();
		} finally {
			setSaving(null);
		}
	}, [comment, currentId, toastSaveFailed]);

	const copyId = useCallback(async () => {
		const id = task?.custom_id ?? task?.id ?? currentId;
		try {
			await navigator.clipboard.writeText(id);
			ToastCommands.createToast({type: 'success', children: i18n._(COPIED_DESCRIPTOR, {id})});
		} catch {
			// clipboard blocked
		}
	}, [task, currentId, i18n]);
	const copyLink = useCallback(async () => {
		if (!task?.url) return;
		try {
			await navigator.clipboard.writeText(task.url);
			ToastCommands.createToast({type: 'success', children: i18n._(LINK_COPIED_DESCRIPTOR)});
		} catch {
			// clipboard blocked
		}
	}, [task?.url, i18n]);

	// Drops on the description append a picture (as ClickUp writes a pasted one) or a link; drops on
	// the composer append to the draft.
	const descDrop = useDropUpload(currentId, (attachment) => {
		if (!attachment.url) return;
		const added = attachment.mimetype?.startsWith('image/')
			? `![${attachment.title.replace(/[[\]]/g, '')}](${attachment.url})`
			: `${attachment.title}: ${attachment.url}`;
		setDescription((current) => `${current.trimEnd()}${current.trim() ? '\n\n' : ''}${added}`);
		setDescDirty(true);
	});
	const commentDrop = useDropUpload(currentId, (attachment) => {
		if (!attachment.url) return;
		setComment((current) => `${current.trimEnd()}${current.trim() ? '\n' : ''}${attachment.url}`);
	});
	// Plain attach (phone ⋯ menu): the file lands in Attachments without touching the text.
	const attachUpload = useDropUpload(currentId, noopUploaded);

	// ── Subtasks ──────────────────────────────────────────────────────────
	const toggleSubtask = useCallback(
		async (subtask: KonvoSubtask) => {
			if (!subtask.id) return;
			setSaving(`sub:${subtask.id}`);
			try {
				await KonvoTasks.setSubtaskDone(currentId, subtask.id, !isTaskClosed(subtask));
			} catch {
				toastSaveFailed();
			} finally {
				setSaving(null);
			}
		},
		[currentId, toastSaveFailed],
	);
	const addSubtask = useCallback(
		async (name: string): Promise<boolean> => {
			const trimmed = name.trim();
			if (!trimmed) return false;
			setSaving('subtask:new');
			try {
				await KonvoTasks.createSubtask(currentId, trimmed);
				return true;
			} catch {
				toastSaveFailed();
				return false;
			} finally {
				setSaving(null);
			}
		},
		[currentId, toastSaveFailed],
	);

	// ── Time tracking ─────────────────────────────────────────────────────
	const timeInfo = KonvoTasks.getTime(currentId);
	const runningEntry = KonvoTasks.runningEntry ?? null;
	const runningHere = runningEntry != null && task != null && runningEntry.task.id === task.id;
	const timerAvailable = KonvoTasks.timerAvailable ?? timeInfo?.own_token ?? true;
	const now = useClock(runningHere);
	const timerAction = useCallback(
		async (label: string, action: () => Promise<unknown>, successText?: string): Promise<boolean> => {
			setSaving(label);
			try {
				await action();
				if (successText) ToastCommands.createToast({type: 'success', children: successText});
				return true;
			} catch (error) {
				const code = error instanceof KonvoTimeTrackingError ? error.code : 'failed';
				ToastCommands.createToast({
					type: 'error',
					children: code === 'link_required' ? i18n._(TIMER_LINK_REQUIRED_DESCRIPTOR) : i18n._(TIME_FAILED_DESCRIPTOR),
				});
				return false;
			} finally {
				setSaving(null);
			}
		},
		[i18n],
	);
	const startTimer = useCallback(
		() => void timerAction('timer', () => KonvoTasks.startTimer(currentId)),
		[timerAction, currentId],
	);
	const stopTimer = useCallback(
		() => void timerAction('timer', () => KonvoTasks.stopTimer(), i18n._(TIME_LOGGED_DESCRIPTOR)),
		[timerAction, i18n],
	);
	const pauseTimer = useCallback(() => void timerAction('timer', () => KonvoTasks.pauseTimer()), [timerAction]);
	const deleteEntry = useCallback(
		async (entry: KonvoTimeEntry) => {
			if (!entry.id) return false;
			return await timerAction(`entry:${entry.id}`, () => KonvoTasks.deleteTimeEntry(currentId, entry.id!));
		},
		[timerAction, currentId],
	);
	const isOwnEntry = useCallback(
		(entry: KonvoTimeEntry) =>
			entry.user?.id != null &&
			timeInfo?.clickup_user_id != null &&
			String(entry.user.id) === timeInfo.clickup_user_id &&
			Boolean(entry.id) &&
			!entry.running,
		[timeInfo?.clickup_user_id],
	);
	const beginLog = useCallback((entry: KonvoTimeEntry | null = null) => {
		if (!entry) {
			setTimeDraft(EMPTY_DRAFT);
			return;
		}
		const start = new Date(entry.start ?? Date.now());
		setTimeDraft({
			entry,
			duration: formatDuration(entry.duration),
			note: entry.description,
			start: new Date(start.getTime() - start.getTimezoneOffset() * 60000).toISOString().slice(0, 16),
		});
	}, []);
	const submitLog = useCallback(async (): Promise<boolean> => {
		if (saving) return false;
		const parsed = parseDuration(timeDraft.duration);
		const duration = parsed == null ? null : Math.round(parsed);
		const startMs = timeDraft.start ? new Date(timeDraft.start).getTime() : null;
		if (
			duration == null ||
			duration < 60_000 ||
			duration > 86_400_000 ||
			(timeDraft.start && !Number.isFinite(startMs)) ||
			(timeDraft.entry != null && startMs == null)
		) {
			ToastCommands.createToast({type: 'error', children: i18n._(INVALID_DURATION_DESCRIPTOR)});
			return false;
		}
		const {entry, note} = timeDraft;
		const ok = await timerAction(
			'log',
			async () => {
				if (entry?.id) await KonvoTasks.updateTimeEntry(currentId, entry.id, duration, startMs!, note.trim());
				else await KonvoTasks.logTime(currentId, duration, note.trim(), startMs ?? undefined);
			},
			i18n._(TIME_LOGGED_DESCRIPTOR),
		);
		if (ok) setTimeDraft(EMPTY_DRAFT);
		return ok;
	}, [saving, timeDraft, i18n, timerAction, currentId]);

	const members = KonvoClickUp.members;
	const closed = task ? isTaskClosed(task) : false;
	const sortedComments = useMemo(
		() => [...(detail?.comments ?? [])].sort((a, b) => (a.date ?? 0) - (b.date ?? 0)),
		[detail?.comments],
	);

	return {
		currentId,
		openTask: setCurrentId,
		detail,
		task,
		loadError,
		saving,
		closed,
		members,
		comments: sortedComments,
		title,
		setTitle,
		commitTitle,
		description,
		descDirty,
		editDescription,
		saveDescription,
		discardDescription,
		complete,
		setStatus,
		setPriority,
		setDue,
		toggleAssignee,
		comment,
		setComment,
		submitComment,
		copyId,
		copyLink,
		descDrop,
		commentDrop,
		attachUpload,
		toggleSubtask,
		addSubtask,
		timeInfo,
		runningEntry,
		runningHere,
		timerAvailable,
		now,
		startTimer,
		stopTimer,
		pauseTimer,
		deleteEntry,
		isOwnEntry,
		timeDraft,
		setTimeDraft,
		beginLog,
		submitLog,
	};
}

export type TaskWorkspace = ReturnType<typeof useTaskWorkspace>;
